import { createSelector } from 'reselect';

import { selectUserIsAuthenticated } from '@pressreader/authentication-core';
import { selectGetConfigValue } from '@pressreader/config';
import { SponsorshipStatus } from '@pressreader/sponsorship-types';
import { browser } from '@pressreader/utils';

import { MODULE_NAME } from '../constants';

import { State } from './reducer';

const getState = (state: { [MODULE_NAME]: State }) => state[MODULE_NAME];

const getHotspotInfoLoadingStatus = createSelector(getState, state => state.loading);

const getSponsor = createSelector(getState, state => state.sponsor);

const getHotspotEnabled = createSelector(getSponsor, sponsor => !!sponsor?.isHotspotEnabled);

const getHotspotActive = createSelector(getSponsor, sponsor => !!sponsor?.isHotspotActive);

const getHotspotSession = createSelector(getHotspotEnabled, getHotspotActive, (enabled, active) => enabled && active);

const getSponsorshipStatus = createSelector(getSponsor, sponsor => sponsor?.status);

const getSponsorIfAvailable = createSelector(getSponsor, sponsor => {
    if (sponsor?.status === SponsorshipStatus.On) {
        return sponsor;
    }
    return null;
});

const getExpiredSiteId = createSelector(getSponsor, sponsor =>
    sponsor?.expiredHotspot?.showExpiredHotspot ? sponsor.expiredHotspot.siteId : undefined,
);

const selectIsEmbeddedModeEnabled = createSelector(
    getSponsor,
    selectGetConfigValue,
    (sponsor, getConfigValue): boolean =>
        getConfigValue<boolean>('features.embedded.enabled', browser.isWebView) || sponsor?.isEmbeddedModeEnabled || false,
);

const selectIsPersonalizationDisabled = createSelector(
    selectIsEmbeddedModeEnabled,
    selectGetConfigValue,
    (isEmbeddedModeEnabled, getConfigValue) => isEmbeddedModeEnabled || getConfigValue<boolean>('features.authentication.disabled', false),
);

const selectPartnerBannerInfo = createSelector(getState, state => state.partner);

/**
 * Sponsor name fit to show a reader, for branding HotSpot dialogs.
 *
 * Reads `notifications.sponsoredBy` (the HUB field meant for display) and never
 * `ISponsor.name`, which is the sponsor's internal name. Falls back to the name
 * retained by the reducer, because the sponsor is already zeroed by the time the
 * expiry dialog opens. Undefined when no name was ever seen — callers keep their own
 * default rather than rendering an empty header. (#175442)
 */
const selectSponsorDisplayName = createSelector(
    getState,
    (state): string | undefined => state.sponsor?.notifications?.sponsoredBy || state.lastSponsorDisplayName,
);

const selectHotspotToggleVisible = createSelector(selectUserIsAuthenticated, selectGetConfigValue, (isAuthenticated, getConfigValue) => {
    if (getConfigValue<boolean>('features.turnoff.hotspot.disabled', false)) {
        return false;
    }
    const requireAuth = getConfigValue<boolean>('features.hotspot.requireAuth', true);
    return requireAuth ? isAuthenticated : true;
});

/**
 * Whether this reader is barred from switching HotSpots off.
 *
 * `SetHotzoneStatus` records a preference and nothing more. The radiant period that actually
 * grants access is keyed to the account and outlives it, so `SponsorshipManager/get` keeps
 * reporting the session active and the client has no way to end it: the cup stays, the pages
 * stay unlocked, and the welcome dialog reopens on the next sponsor reload (#176290). Product
 * accepted that limitation for anonymous readers rather than shipping a control that lies, so
 * the toggle goes inert for the life of the session and the grant runs to its own expiry.
 *
 * Keyed on `isHotspotActive`, deliberately not on `isHotspotEnabled`. The server never reports
 * the preference flag true for an anonymous session (#175378) — the value the toggle renders is
 * patched in client-side after activation — so gating on it would leave this guard off for
 * exactly the readers it protects, and only until some later reload cleared the patch.
 *
 * Remove once `GetSponsorshipInfo` honours an explicit opt-out and the client can end a session
 * again.
 */
const selectHotspotOptOutBlocked = createSelector(
    selectUserIsAuthenticated,
    getHotspotActive,
    (isAuthenticated, hotspotActive): boolean => !isAuthenticated && hotspotActive,
);

const selectIsHotspotLiveTimerEnabled = createSelector(selectGetConfigValue, (getConfigValue): boolean =>
    getConfigValue<boolean>('features.hotspot.liveTimer', true),
);

export {
    getExpiredSiteId,
    getHotspotActive,
    getHotspotEnabled,
    getHotspotInfoLoadingStatus,
    getHotspotSession,
    getSponsor,
    getSponsorIfAvailable,
    getSponsorshipStatus,
    selectHotspotOptOutBlocked,
    selectHotspotToggleVisible,
    selectIsEmbeddedModeEnabled,
    selectIsHotspotLiveTimerEnabled,
    selectIsPersonalizationDisabled,
    selectPartnerBannerInfo,
    selectSponsorDisplayName,
};
